import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import logo from './ap-logo-4.png';
import './HafLanding.css';

const ACOLS = ['gluten','dairy','eggs','peanuts','nuts','soya','sesame','celery','mustard','sulphites'];
const ACOL_HEADS = { gluten:'Gluten', dairy:'Dairy', eggs:'Eggs', peanuts:'Peanuts', nuts:'Tree Nuts', soya:'Soya', sesame:'Sesame', celery:'Celery', mustard:'Mustard', sulphites:'Sulphites' };

const DEFAULT_MENU = [
  { name:'Vegetable Rice', desc:'Freshly cooked basmati rice with seasonal vegetables and mild spices.', allergens:['Celery'], image_url:'' },
  { name:'Dal & Chapati',  desc:'Protein-rich lentil curry served with freshly made wholemeal chapatis.', allergens:['Gluten','Mustard'], image_url:'' },
  { name:'Pasta Bake',     desc:'Oven-baked pasta in tomato sauce with vegetables. Vegetarian.', allergens:['Gluten','Dairy'], image_url:'' },
  { name:'Fruit Salad',    desc:'Fresh seasonal fruit salad — a nutritious, naturally sweet dessert.', allergens:[], image_url:'' },
];
const DEFAULT_MATRIX = [
  { meal:'Vegetable Rice', gluten:false, dairy:false, eggs:false, peanuts:false, nuts:false, soya:false, sesame:false, celery:true,  mustard:false, sulphites:false },
  { meal:'Dal & Chapati',  gluten:true,  dairy:false, eggs:false, peanuts:false, nuts:false, soya:false, sesame:false, celery:false, mustard:true,  sulphites:false },
  { meal:'Pasta Bake',     gluten:true,  dairy:true,  eggs:false, peanuts:false, nuts:false, soya:false, sesame:false, celery:false, mustard:false, sulphites:false },
  { meal:'Fruit Salad',    gluten:false, dairy:false, eggs:false, peanuts:false, nuts:false, soya:false, sesame:false, celery:false, mustard:false, sulphites:false },
];
const DEFAULT_TESTIMONIALS = [
  { quote:'My children look forward to the HAF meals every holiday. The food is delicious and they come home happy and full.', name:'Sarah M.', role:'Parent, Watford' },
  { quote:'The quality and hygiene standards are exceptional. We are proud to partner with Akshaya Patra UK for the HAF programme.', name:'Mrs. Patel', role:'School Coordinator' },
  { quote:'Such a relief knowing my kids get a hot, nutritious meal during the holidays. A truly brilliant programme.', name:'James K.', role:'Parent, Hemel Hempstead' },
];
const DEFAULT_STATS = [
  { value:'500+', label:'Children Fed',  color:'green' },
  { value:'5★',   label:'Kitchen Rating', color:'blue' },
  { value:'10+',  label:'Venues',         color:'orange' },
];
const DEFAULT_KITCHEN_CARDS = [
  { icon:'🧼', title:'Hygiene Certified',  desc:'Full FSA 5-star hygiene rating. Regular unannounced inspections passed with commendation.' },
  { icon:'🌿', title:'Fresh Ingredients',  desc:'Locally sourced, fresh produce used daily. No artificial additives or preservatives.' },
  { icon:'📋', title:'Allergen Control',   desc:'All 14 major allergens tracked, declared, and strictly managed across our kitchen.' },
  { icon:'👨‍🍳', title:'Trained Chefs',     desc:'Level 2 Food Hygiene certified kitchen team with decades of combined experience.' },
];

const fmtPrice = v => { if (!v) return ''; const s = String(v).trim(); return s.startsWith('£') ? s : `£${s}`; };

const HafLanding = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [lbItem, setLbItem] = useState(null);

  const [hero, setHero] = useState({
    eyebrow: 'Holiday Activities & Food Programme',
    headline: 'Nutritious Meals.\nHappy Children.\nThriving Communities.',
    body: 'Akshaya Patra UK delivers free, nutritious, 5-star hygiene-rated meals to children during school holidays through the government-funded HAF programme.',
    cta1_text: 'Login / Order Now',
    cta2_text: 'Learn More',
    image_url: '',
  });
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [about, setAbout] = useState({ title:'About Our HAF Programme', body:'The Holiday Activities and Food (HAF) programme is funded by the Department for Education and provides free meals, physical activities, and enriching experiences to children on free school meals during school holidays.' });
  const [kitchen, setKitchen] = useState({ title:'Our 5-Star Rated Kitchen', body:'Our kitchen holds the highest possible Food Standards Agency hygiene rating. Every meal is prepared to the strictest safety standards by our Level 2 Food Hygiene certified team.', cards: DEFAULT_KITCHEN_CARDS });
  const [menuItems, setMenuItems] = useState(DEFAULT_MENU);
  const [allergenMatrix, setAllergenMatrix] = useState(DEFAULT_MATRIX);
  const [allergenNote, setAllergenNote] = useState('⚠ May contain traces due to shared kitchen equipment. Always notify our team of severe allergies.');
  const [testimonials, setTestimonials] = useState(DEFAULT_TESTIMONIALS);
  const [gallery, setGallery] = useState([]);
  const [contact, setContact] = useState({ email:'info@akshayapatramealorders.co.uk', phone:'', address:'Akshaya Patra Kitchen, Watford, UK', deadline:"Orders must be placed by Friday for the following week's deliveries.", map_url:'', cta_text:'Login / Order Now' });
  const [seasonal, setSeasonal] = useState({ open: false, headline:'🎉 Orders Now Open — Summer HAF 2025!', body:'We are now accepting orders for the Summer Holiday Activities and Food programme. Place your order before the deadline to secure your allocation.', dates:'', cta_text:'Order Now', cta_url:'/login' });
  const [pricing, setPricing] = useState({ show: true, headline:'Meal Costs & Delivery', meal_cost:'', delivery_cost:'', subsidy_note:'Meals are provided at a subsidised cost through the government-funded HAF programme. Eligible families pay a reduced rate — please contact us to confirm your allocation and pricing.', notes:'' });
  const [temp, setTemp] = useState({ show: true, headline:'IoT Temperature Monitoring', body:'Every meal we produce is tracked in real-time using our IoT-enabled temperature monitoring system. We strictly maintain food at above 63°C throughout cooking, holding, and dispatch — fully compliant with UK food safety law and FSA guidelines.', min_temp:'63°C', image_url:'', points:['Real-time IoT sensor alerts if temperature drops below threshold','Full digital audit trail for every batch — available on request','Strict hot-holding throughout dispatch and delivery','All records retained for compliance and HAF reporting'] });
  const [downloads, setDownloads] = useState({ items: [] });
  const carouselRef = useRef();

  useEffect(() => {
    const sections = ['hero','about','kitchen','menu','allergens','testimonials','gallery','contact','seasonal','pricing','downloads','temp'];
    Promise.all(sections.map(s => getDoc(doc(db, 'haf_content', s))))
      .then(snaps => {
        const data = {};
        sections.forEach((s,i) => { if (snaps[i].exists()) data[s] = snaps[i].data(); });
        if (data.hero)         setHero(h => ({ ...h, ...data.hero }));
        if (data.about) {
          setAbout(a => ({ ...a, ...data.about }));
          if (data.about.stats) setStats(data.about.stats);
        }
        if (data.kitchen)      setKitchen(k => ({ ...k, ...data.kitchen }));
        if (data.menu?.items)  setMenuItems(data.menu.items);
        if (data.allergens?.matrix) setAllergenMatrix(data.allergens.matrix);
        if (data.allergens?.note)   setAllergenNote(data.allergens.note);
        if (data.testimonials?.items) setTestimonials(data.testimonials.items);
        if (data.gallery?.items)      setGallery(data.gallery.items);
        if (data.contact)      setContact(c => ({ ...c, ...data.contact }));
        if (data.seasonal)     setSeasonal(s => ({ ...s, ...data.seasonal }));
        if (data.pricing)      setPricing(p => ({ ...p, ...data.pricing }));
        if (data.downloads)    setDownloads(d => ({ ...d, ...data.downloads }));
        if (data.temp)         setTemp(t => ({ ...t, ...data.temp }));
      })
      .catch(e => console.warn('HAF content load error:', e.message))
      .finally(() => setLoading(false));
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  if (loading) return (
    <div className="haf-loader">
      <div className="haf-spinner"></div>
      <p>Akshaya Patra UK</p>
    </div>
  );

  return (
    <div className="haf-page">

      {/* NAV */}
      <nav className="haf-nav">
        <div className="haf-nav-logo" onClick={() => window.scrollTo({ top:0, behavior:'smooth' })}>
          <img src={logo} alt="Akshaya Patra UK"/>
        </div>
        <div className="haf-nav-links">
          <a href="#about"        onClick={e=>{ e.preventDefault(); scrollTo('haf-about'); }}>About HAF</a>
          <a href="#kitchen"      onClick={e=>{ e.preventDefault(); scrollTo('haf-kitchen'); }}>Our Kitchen</a>
          <a href="#menu"         onClick={e=>{ e.preventDefault(); scrollTo('haf-menu'); }}>Menu</a>
          <a href="#allergens"    onClick={e=>{ e.preventDefault(); scrollTo('haf-allergens'); }}>Allergens</a>
          <a href="#testimonials" onClick={e=>{ e.preventDefault(); scrollTo('haf-testimonials'); }}>Stories</a>
          <a href="/volunteer" onClick={e=>{ e.preventDefault(); navigate('/volunteer'); }}>Volunteer</a>
          <a href="#contact"      onClick={e=>{ e.preventDefault(); scrollTo('haf-contact'); }}>Contact</a>
          <a className="haf-nav-cta" onClick={() => navigate('/login')}>Login / Order Now</a>
        </div>
        <button className="haf-nav-burger" onClick={() => setMobileOpen(o => !o)} aria-label="Menu">☰</button>
      </nav>

      {/* SEASONAL BANNER */}
      {seasonal.open && (
        <div className="haf-seasonal-banner">
          <div className="haf-seasonal-inner">
            <div>
              <div className="haf-seasonal-headline">{seasonal.headline}</div>
              {seasonal.dates && <div className="haf-seasonal-dates">📅 {seasonal.dates}</div>}
              {seasonal.body && <div className="haf-seasonal-body">{seasonal.body}</div>}
            </div>
            <button className="haf-seasonal-cta" onClick={() => navigate(seasonal.cta_url || '/login')}>
              {seasonal.cta_text || 'Order Now'}
            </button>
          </div>
        </div>
      )}

      {/* MOBILE NAV */}
      <div className={`haf-mobile-nav${mobileOpen ? ' open' : ''}`}>
        <a href="#about"        onClick={e=>{ e.preventDefault(); scrollTo('haf-about'); }}>About HAF</a>
        <a href="#kitchen"      onClick={e=>{ e.preventDefault(); scrollTo('haf-kitchen'); }}>Our Kitchen</a>
        <a href="#menu"         onClick={e=>{ e.preventDefault(); scrollTo('haf-menu'); }}>Menu</a>
        <a href="#allergens"    onClick={e=>{ e.preventDefault(); scrollTo('haf-allergens'); }}>Allergens</a>
        <a href="#testimonials" onClick={e=>{ e.preventDefault(); scrollTo('haf-testimonials'); }}>Stories</a>
        <a href="/volunteer" onClick={e=>{ e.preventDefault(); navigate('/volunteer'); }}>Volunteer</a>
        <a href="#contact"      onClick={e=>{ e.preventDefault(); scrollTo('haf-contact'); }}>Contact</a>
        <a className="haf-nav-cta" style={{display:'inline-block',marginTop:4}} onClick={() => navigate('/login')}>Login / Order Now</a>
      </div>

      {/* HERO */}
      <section className="haf-hero">
        <div className="haf-hero-left">
          <div className="haf-eyebrow">{hero.eyebrow}</div>
          <h1 className="haf-headline">
            {hero.headline.split('\n').map((line,i,arr) => (
              <React.Fragment key={i}>{line}{i < arr.length-1 && <br/>}</React.Fragment>
            ))}
          </h1>
          <p className="haf-hero-body">{hero.body}</p>
          <div className="haf-hero-btns">
            <button className="haf-btn-green" onClick={() => navigate('/login')}>
              {hero.cta1_text || 'Login / Order Now'}
            </button>
            <button className="haf-btn-white" onClick={() => scrollTo('haf-about')}>
              {hero.cta2_text || 'Learn More'}
            </button>
          </div>
        </div>
        <div className="haf-hero-right">
          {hero.image_url
            ? <img src={hero.image_url} alt="HAF Programme"/>
            : <div className="haf-hero-ph">HAF</div>
          }
        </div>
      </section>

      {/* STATS */}
      <div className="haf-stats">
        <div className="haf-stats-grid">
          {stats.filter(s => !/free\s*meals/i.test(s.label || '')).map((s,i) => (
            <div key={i} className="haf-stat">
              <div className={`haf-stat-num ${s.color||'green'}`}>{s.value}</div>
              <div className="haf-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* PRICING */}
      {pricing.show && (
        <section className="haf-section haf-pricing-section" id="haf-pricing">
          <div className="haf-container">

            {/* Intro */}
            <div className="haf-pricing-intro">
              <h2 className="haf-section-title">{pricing.headline || 'Meal Costs & Delivery'}</h2>
              <div className="haf-underline"></div>
              <p className="haf-pricing-subtitle">
                {pricing.subtitle || 'Freshly prepared meals for eligible families, with clear pricing and delivery support through the HAF programme.'}
              </p>
            </div>

            {/* Price cards — only shown if admin has set prices */}
            {(pricing.meal_cost || pricing.delivery_cost) && (
              <div className="haf-price-cards">
                {pricing.meal_cost && (
                  <div className="haf-price-card">
                    <div className="haf-price-card-top">
                      <div className="haf-price-icon-wrap">
                        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/>
                          <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/>
                        </svg>
                      </div>
                      <div>
                        <div className="haf-price-label">Meal cost</div>
                        <div className="haf-price-amount">{fmtPrice(pricing.meal_cost)}</div>
                        <div className="haf-price-vat">{pricing.meal_cost_desc || 'Per meal · including VAT'}</div>
                      </div>
                    </div>
                    <div className="haf-price-divider"/>
                    <ul className="haf-price-includes">
                      <li>Freshly cooked in our FSA 5-star rated kitchen</li>
                      <li>All 14 major allergens declared on every meal</li>
                      <li>Suitable for vegetarians and most dietary needs</li>
                      <li>Minimum order quantities — contact us to discuss</li>
                    </ul>
                  </div>
                )}
                {pricing.delivery_cost && (
                  <div className="haf-price-card">
                    <div className="haf-price-card-top">
                      <div className="haf-price-icon-wrap">
                        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="1" y="3" width="15" height="13" rx="1"/>
                          <path d="M16 8h4l3 3v5h-7V8z"/>
                          <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                        </svg>
                      </div>
                      <div>
                        <div className="haf-price-label">Delivery</div>
                        <div className="haf-price-amount">{fmtPrice(pricing.delivery_cost)}</div>
                        <div className="haf-price-vat">{pricing.delivery_cost_desc || 'Per order · including VAT'}</div>
                      </div>
                    </div>
                    <div className="haf-price-divider"/>
                    <ul className="haf-price-includes">
                      <li>Direct to your school or delivery address</li>
                      <li>Temperature-controlled throughout transit</li>
                      <li>Timed delivery windows — no waiting around</li>
                      <li>Tracked, reliable and contact-free option</li>
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Trust strip */}
            <div className="haf-pricing-trust">
              <div className="haf-pricing-trust-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="#7ab648" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>
                <span>FSA 5-Star Rated Kitchen</span>
              </div>
              <div className="haf-pricing-trust-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="#7ab648" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>
                <span>Freshly Cooked Every Day</span>
              </div>
              <div className="haf-pricing-trust-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="#7ab648" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>
                <span>All Allergens Declared</span>
              </div>
              <div className="haf-pricing-trust-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="#7ab648" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>
                <span>Government-Funded HAF</span>
              </div>
            </div>

            {/* Subsidy bar */}
            {pricing.subsidy_note && (
              <div className="haf-subsidy-bar">
                <div className="haf-subsidy-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    <path d="M9 12l2 2 4-4"/>
                  </svg>
                </div>
                <div className="haf-subsidy-body">
                  <div className="haf-subsidy-title">HAF Programme — Subsidised Pricing</div>
                  <p className="haf-subsidy-text">{pricing.subsidy_note}</p>
                </div>
                <button className="haf-subsidy-cta" onClick={() => scrollTo('haf-contact')}>
                  Confirm eligibility
                </button>
              </div>
            )}

          </div>
        </section>
      )}

      {/* ABOUT */}
      <section className="haf-section haf-white" id="haf-about">
        <div className="haf-container">
          <h2 className="haf-section-title">{about.title}</h2>
          <div className="haf-underline"></div>
          <p className="haf-lead">{about.body}</p>
        </div>
      </section>

      {/* 5-STAR KITCHEN */}
      <section className="haf-section haf-grey" id="haf-kitchen">
        <div className="haf-container">
          <h2 className="haf-section-title">{kitchen.title}</h2>
          <div className="haf-underline"></div>
          <p className="haf-lead">{kitchen.body}</p>

          <div className="haf-kitchen-layout">
            {/* FSA sticker */}
            <div className="haf-kitchen-left">
              <a
                href="https://ratings.food.gov.uk/business/1284719/the-akshaya-patra-uk-limited-imperial-way"
                target="_blank" rel="noopener noreferrer"
                className="haf-fsa-sticker" aria-label="View our Food Hygiene Rating on food.gov.uk"
              >
                <div className="haf-fsa-body">
                  <div className="haf-fsa-title">FOOD HYGIENE RATING</div>
                  <div className="haf-fsa-rule" aria-hidden="true" />
                  <div className="haf-fsa-numbers">
                    {[0,1,2,3,4,5].map(n => (
                      <div key={n} className={`haf-fsa-num${n === 5 ? ' haf-fsa-num-active' : ''}`}>{n}</div>
                    ))}
                  </div>
                </div>
                <div className="haf-fsa-footer">VERY GOOD</div>
              </a>
              <div className="haf-fsa-caption">
                Verified — <a href="https://ratings.food.gov.uk/business/1284719/the-akshaya-patra-uk-limited-imperial-way" target="_blank" rel="noopener noreferrer">View our FSA record ↗</a>
              </div>
            </div>

            {/* Kitchen photo */}
            <div className="haf-kitchen-right">
              {kitchen.image_url
                ? <img src={kitchen.image_url} alt="Our kitchen" className="haf-kitchen-photo"/>
                : <div className="haf-kitchen-photo-ph">Kitchen photo coming soon</div>
              }
            </div>
          </div>
        </div>
      </section>

      {/* IOT TEMPERATURE */}
      {temp.show && (
        <section className="haf-section haf-navy" id="haf-temp">
          <div className="haf-container">
            <h2 className="haf-section-title">{temp.headline}</h2>
            <div className="haf-underline"></div>
            <div className="haf-temp-layout">
              {temp.image_url && (
                <div className="haf-temp-img-wrap">
                  <img src={temp.image_url} alt="IoT temperature monitoring"/>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* MENU */}
      <section className="haf-section haf-white" id="haf-menu">
        <div className="haf-container">
          <h2 className="haf-section-title">Our Menu</h2>
          <div className="haf-underline"></div>
          <p className="haf-lead">Freshly prepared meals designed by nutritionists to meet children's dietary needs. All meals are free of charge for eligible children.</p>
          <div className="haf-carousel-wrap">
            <button className="haf-carousel-arrow haf-arrow-left" onClick={() => carouselRef.current.scrollBy({ left: -300, behavior:'smooth' })} aria-label="Previous">‹</button>
            <div className="haf-menu-carousel" ref={carouselRef}>
              {menuItems.map((item,i) => {
                const slug = item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                return (
                  <div key={i} className="haf-menu-card haf-menu-card-link" onClick={() => navigate(`/meal/${slug}`)} role="button" tabIndex={0} onKeyDown={e => e.key==='Enter' && navigate(`/meal/${slug}`)}>
                    <div className="haf-menu-img">
                      {item.image_url
                        ? <img src={item.image_url} alt={item.name}/>
                        : <div className="haf-menu-img-ph">🍽</div>
                      }
                    </div>
                    <div className="haf-menu-body">
                      <div className="haf-menu-name">{item.name}</div>
                      <p className="haf-menu-desc">{item.desc || item.description || ''}</p>
                      {item.allergens && item.allergens.length > 0 && (
                        <div className="haf-a-tags">
                          {item.allergens.map(a => <span key={a} className="haf-a-tag">{a}</span>)}
                        </div>
                      )}
                      <div className="haf-menu-card-cta">View Meal Details →</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <button className="haf-carousel-arrow haf-arrow-right" onClick={() => carouselRef.current.scrollBy({ left: 300, behavior:'smooth' })} aria-label="Next">›</button>
          </div>
          {menuItems[0]?.pdf_url && (
            <div className="haf-menu-pdf">
              <a href={menuItems[0].pdf_url} target="_blank" rel="noopener noreferrer" className="haf-pdf-btn">
                📄 Download Full Menu PDF
              </a>
            </div>
          )}
        </div>
      </section>

      {/* ALLERGEN MATRIX */}
      <section className="haf-section haf-grey" id="haf-allergens">
        <div className="haf-container">
          <h2 className="haf-section-title">Allergen Information</h2>
          <div className="haf-underline"></div>
          <p className="haf-lead">All 14 major allergens are tracked and declared. Please inform our team of any dietary requirements when placing your order.</p>
          <div className="haf-tbl-wrap">
            <table className="haf-a-tbl">
              <thead>
                <tr>
                  <th>Meal</th>
                  {ACOLS.map(c => <th key={c}>{ACOL_HEADS[c]}</th>)}
                </tr>
              </thead>
              <tbody>
                {allergenMatrix.map((row,i) => (
                  <tr key={i}>
                    <td>{row.meal}</td>
                    {ACOLS.map(c => (
                      <td key={c}>
                        {row[c] === true  ? <span className="haf-yes">✓</span>
                        : row[c] === 'may' ? <span className="haf-may">M</span>
                        : <span className="haf-no">–</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="haf-tbl-note">{allergenNote}</p>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="haf-section haf-blue-bg" id="haf-testimonials">
        <div className="haf-container">
          <h2 className="haf-section-title">What Families Say</h2>
          <div className="haf-underline"></div>
          <div className="haf-testi-grid">
            {testimonials.map((t,i) => (
              <div key={i} className="haf-testi-card">
                <p className="haf-testi-quote">"{t.quote}"</p>
                <div className="haf-testi-name">{t.name}</div>
                <div className="haf-testi-role">{t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      {gallery.length > 0 && (
        <section className="haf-section haf-white">
          <div className="haf-container">
            <h2 className="haf-section-title">Gallery</h2>
            <div className="haf-underline"></div>
            <div className="haf-gallery-grid">
              {gallery.map((item,i) => {
                const isVid = item.type === 'video' || /\.(mp4|webm|ogg)$/i.test(item.url || '');
                return (
                  <div key={i} className="haf-gallery-item" onClick={() => setLbItem(item)}>
                    {isVid
                      ? <video src={item.url} muted playsInline/>
                      : <img src={item.url} alt={item.caption || ''}/>
                    }
                    {item.caption && <div className="haf-gallery-cap">{item.caption}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* DOWNLOADS */}
      {downloads.items && downloads.items.length > 0 && (
        <section className="haf-section haf-grey" id="haf-downloads">
          <div className="haf-container">
            <h2 className="haf-section-title">Information & Documents</h2>
            <div className="haf-underline"></div>
            <p className="haf-lead">Everything you need to know about our HAF programme. Download our information packs, menus, and kitchen safety documents — no need to call us.</p>
            <div className="haf-downloads-grid">
              {downloads.items.map((item,i) => (
                <div key={i} className="haf-download-card">
                  <div className="haf-dl-icon">
                    {item.type === 'pdf' ? '📄' : item.type === 'image' ? '🖼' : '📎'}
                  </div>
                  <div className="haf-dl-body">
                    <div className="haf-dl-title">{item.title}</div>
                    {item.description && <p className="haf-dl-desc">{item.description}</p>}
                  </div>
                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="haf-dl-btn" download>
                    ⬇ Download
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONTACT */}
      <section className="haf-section haf-navy" id="haf-contact">
        <div className="haf-container">
          <h2 className="haf-section-title">Get In Touch</h2>
          <div className="haf-underline"></div>
          <div className="haf-contact-grid">
            <div>
              <div className="haf-c-item">
                <div className="haf-c-icon">📧</div>
                <div className="haf-c-info"><h4>Email</h4><p>{contact.email}</p></div>
              </div>
              {contact.phone && (
                <div className="haf-c-item">
                  <div className="haf-c-icon">📞</div>
                  <div className="haf-c-info"><h4>Phone</h4><p>{contact.phone}</p></div>
                </div>
              )}
              <div className="haf-c-item">
                <div className="haf-c-icon">📍</div>
                <div className="haf-c-info"><h4>Kitchen Address</h4><p>{contact.address}</p></div>
              </div>
              <div className="haf-c-item">
                <div className="haf-c-icon">🕐</div>
                <div className="haf-c-info"><h4>Order Deadline</h4><p>{contact.deadline}</p></div>
              </div>
              <button className="haf-btn-green" style={{marginTop:20}} onClick={() => navigate('/login')}>
                {contact.cta_text || 'Login / Order Now'}
              </button>
            </div>
            <div className="haf-map-ph">
              {contact.map_url
                ? <iframe src={contact.map_url} allowFullScreen title="Kitchen location" loading="lazy"/>
                : '📍 Contact us for directions'
              }
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="haf-footer">
        <p>
          © {new Date().getFullYear()} Akshaya Patra UK — Holiday Activities and Food Programme &nbsp;·&nbsp;
          <a href="https://akshayapatra.org.uk" target="_blank" rel="noopener noreferrer">Main Website</a>
        </p>
      </footer>

      {/* LIGHTBOX */}
      {lbItem && (
        <div className={`haf-lightbox open`} onClick={() => setLbItem(null)}>
          <button className="haf-lb-close" onClick={() => setLbItem(null)}>✕</button>
          <div className="haf-lightbox-content" onClick={e => e.stopPropagation()}>
            {lbItem.type === 'video' || /\.(mp4|webm|ogg)$/i.test(lbItem.url || '')
              ? <video src={lbItem.url} controls autoPlay/>
              : <img src={lbItem.url} alt={lbItem.caption || ''}/>
            }
          </div>
        </div>
      )}

    </div>
  );
};

export default HafLanding;
